// ===========================================
// Public Permission Slip (magic link)
// ===========================================
// UNAUTHENTICATED. The token in the URL is the whole credential — the same
// trust model as a paper slip in a schoolbag, chosen deliberately because
// forcing a login is where response rates die on a deadline-driven form.
//
// The page therefore shows only what the signing decision needs: the slip, and
// this family's own children. No school-wide data, no other families.
//
// Framed by the default public template (`PublicPageShell`, 2026-10-09), the
// same as the enquiry and Meet & Greet pages. This page is reached from an
// email with no portal shell and no login, so a recognisable school page is
// part of what tells a parent this is a real consent request and not phishing.
// Every state (loading, an expired link, the form) sits inside the frame.

import { useParams } from 'react-router-dom';
import { Alert, Box, Card, CardContent, CircularProgress, Container, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import PublicPageShell from '../../components/public/PublicPageShell';
import SlipSigningForm from '../../components/permissionSlips/SlipSigningForm';
import {
  usePublicPermissionSlip,
  useSignPublicPermissionSlip,
  useDeclinePublicPermissionSlip,
} from '../../hooks/usePermissionSlips';
import type { SignSlipInput } from '../../api/permissionSlips.api';

export default function PublicPermissionSlipPage() {
  const { token = '', schoolSlug } = useParams<{ token: string; schoolSlug: string }>();

  const { data, isLoading, isError } = usePublicPermissionSlip(token);
  const signMutation = useSignPublicPermissionSlip();
  const declineMutation = useDeclinePublicPermissionSlip();

  const handleSign = async (input: SignSlipInput) => {
    await signMutation.mutateAsync({ token, data: input });
  };

  const handleDecline = async (studentId: string, parentNotes?: string) => {
    await declineMutation.mutateAsync({ token, studentId, parentNotes });
  };

  const ready = !isLoading && !isError && !!data;

  return (
    <PublicPageShell
      title="Permission Slip"
      subtitle={ready ? 'Please review and respond for each child below.' : undefined}
      schoolName={data?.school?.name ?? undefined}
      schoolSlug={schoolSlug}
    >
      <Container maxWidth="md" sx={{ pb: 4, position: 'relative', zIndex: 1, flex: 1 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : isError || !data ? (
          // Deliberately vague: the server returns the same 404 for an unknown
          // token and an expired one, so that probing tokens reveals nothing.
          <Alert severity="error" icon={<LockIcon />}>
            This link is no longer valid. It may have expired, or the school may have closed the
            permission slip. Please contact the school, or sign in to your parent portal.
          </Alert>
        ) : (
          <>
            <SlipSigningForm
              slip={data.slip}
              responses={data.responses}
              onSign={handleSign}
              onDecline={handleDecline}
              isSubmitting={signMutation.isPending || declineMutation.isPending}
            />

            <Card elevation={0} sx={{ mt: 4, bgcolor: 'background.paper' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Your name, the date and time, and your network details are recorded with your
                  response. You can also sign in to your parent portal to view your signed slips at
                  any time.
                </Typography>
              </CardContent>
            </Card>
          </>
        )}
      </Container>
    </PublicPageShell>
  );
}
